/**
 * Redirect URL management utilities for handling login redirects
 *
 * How it works:
 * 1. When logged-out user visits voting page → Router guard saves URL to session storage
 * 2. User gets redirected to login page
 * 3. After successful login → Redirect back to saved URL
 * 4. Session storage ensures URL persists across page reloads
 *
 * Supported voting pages (new format):
 * - /projects/:projectId/clips/:clipId/user-voting/clip-selection
 * - /projects/:projectId/clips/:clipId/user-voting/title
 * - /projects/:projectId/clips/:clipId/user-voting/header
 * - /projects/:projectId/clips/:clipId/user-voting/final-edit
 *
 * Legacy voting pages (redirected to new format):
 * - /user-voting/clip-selection/:projectId/:clipId
 * - /user-voting/title/:projectId/:clipId
 * - /user-voting/header/:projectId/:clipId
 * - /user-voting/final-edit/:projectId/:clipId
 */

const REDIRECT_URL_KEY = 'clipflow_redirect_url'

/**
 * Save the current URL to session storage for redirect after login
 * @param url - The URL to redirect to after login
 */
export const saveRedirectUrl = (url: string): void => {
  try {
    sessionStorage.setItem(REDIRECT_URL_KEY, url)
    console.log('🔄 Saved redirect URL:', url)
  } catch (error) {
    console.warn('Failed to save redirect URL to session storage:', error)
  }
}

/**
 * Get the saved redirect URL from session storage
 * @returns The saved redirect URL or null if none exists
 */
export const getRedirectUrl = (): string | null => {
  try {
    const url = sessionStorage.getItem(REDIRECT_URL_KEY)
    console.log('🔄 Retrieved redirect URL:', url)
    return url
  } catch (error) {
    console.warn('Failed to retrieve redirect URL from session storage:', error)
    return null
  }
}

/**
 * Clear the saved redirect URL from session storage
 */
export const clearRedirectUrl = (): void => {
  try {
    sessionStorage.removeItem(REDIRECT_URL_KEY)
    console.log('🔄 Cleared redirect URL')
  } catch (error) {
    console.warn('Failed to clear redirect URL from session storage:', error)
  }
}

/**
 * Check if the given URL is a user voting page that should trigger redirect saving
 * @param url - The URL to check
 * @returns True if the URL is a user voting page
 */
export const isUserVotingPage = (url: string): boolean => {
  const votingPagePatterns = [
    // New URL format
    '/clips/',
    '/user-voting/clip-selection',
    '/user-voting/title',
    '/user-voting/header',
    '/user-voting/final-edit',
  ]

  // Check if URL contains /clips/ AND one of the user-voting patterns
  const hasClipsPath = url.includes('/clips/')
  const hasVotingPattern = votingPagePatterns.slice(1).some((pattern) => url.includes(pattern))

  return hasClipsPath && hasVotingPattern
}

/**
 * Get the redirect URL after successful login, with fallback to dashboard
 * @returns The URL to redirect to
 */
export const getPostLoginRedirectUrl = (): string => {
  const savedUrl = getRedirectUrl()

  if (savedUrl && isUserVotingPage(savedUrl)) {
    clearRedirectUrl()
    return savedUrl
  }

  // Clear any invalid redirect URL
  if (savedUrl) {
    clearRedirectUrl()
  }

  return '/dashboard'
}
